import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  Calendar, 
  MapPin, 
  Trophy, 
  Filter, 
  Search,
  ChevronRight,
  Menu,
  X,
  ArrowLeft
} from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Navbar
const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  
  return (
    <nav className="fixed top-0 left-0 right-0 z-50 glass">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          <Link to="/" className="flex items-center gap-2">
            <span className="font-heading text-2xl font-bold">
              <span className="text-primary">MICHEZO</span>
              <span className="text-secondary">NET</span>
            </span>
          </Link>
          
          <div className="hidden md:flex items-center gap-8">
            <Link to="/events" className="text-primary font-body text-sm">Events</Link>
            <Link to="/directory" className="text-white/70 hover:text-primary transition-colors font-body text-sm">Directory</Link>
            <Link to="/login" className="btn-secondary flex items-center justify-center">Login</Link>
            <Link to="/signup" className="btn-primary flex items-center justify-center">Get Started</Link>
          </div>
          
          <button className="md:hidden text-white" onClick={() => setIsOpen(!isOpen)}>
            {isOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
        
        {isOpen && (
          <div className="md:hidden pb-6 flex flex-col gap-4">
            <Link to="/events" className="text-primary py-2" onClick={() => setIsOpen(false)}>Events</Link>
            <Link to="/directory" className="text-white/70 py-2" onClick={() => setIsOpen(false)}>Directory</Link>
            <Link to="/login" className="btn-secondary w-full text-center" onClick={() => setIsOpen(false)}>Login</Link>
            <Link to="/signup" className="btn-primary w-full text-center" onClick={() => setIsOpen(false)}>Get Started</Link>
          </div>
        )}
      </div>
    </nav>
  );
};

const EventsPage = () => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [sportFilter, setSportFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState('');

  const sports = ['Football', 'Basketball', 'Athletics', 'Rugby', 'Golf', 'Tennis', 'Swimming', 'Cricket'];
  const eventTypes = ['Tournament', 'League', 'Championship', 'Qualifier', 'Friendly', 'Training Camp'];

  useEffect(() => {
    const fetchEvents = async () => {
      try {
        const response = await axios.get(`${API}/events`);
        setEvents(response.data);
      } catch (err) {
        // Placeholder data
        setEvents([
          { id: '1', name: 'Kenya Premier League 2026', sport: 'Football', start_date: '2026-03-15', end_date: '2026-11-30', event_type: 'League', venue: { name: 'Kasarani Stadium' } },
          { id: '2', name: 'East Africa Basketball Championship', sport: 'Basketball', start_date: '2026-04-01', end_date: '2026-04-10', event_type: 'Championship', venue: { name: 'Nyayo Stadium' } },
          { id: '3', name: 'Road to SomaBay Qualifier - Nairobi', sport: 'Golf', start_date: '2026-03-20', end_date: '2026-03-22', event_type: 'Qualifier', venue: { name: 'Karen Country Club' } },
          { id: '4', name: 'African Youth Athletics Meet', sport: 'Athletics', start_date: '2026-05-10', end_date: '2026-05-15', event_type: 'Championship', venue: { name: 'Moi International Sports Centre' } },
          { id: '5', name: 'Safari Rugby Sevens', sport: 'Rugby', start_date: '2026-04-25', end_date: '2026-04-27', event_type: 'Tournament', venue: { name: 'RFUEA Ground' } },
          { id: '6', name: 'Pan-African Swimming Championship', sport: 'Swimming', start_date: '2026-06-01', end_date: '2026-06-05', event_type: 'Championship', venue: { name: 'Kasarani Aquatic Centre' } }
        ]);
      } finally {
        setLoading(false);
      }
    };
    fetchEvents();
  }, []);

  const filteredEvents = events.filter(event => {
    const matchesSearch = event.name.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesSport = !sportFilter || event.sport === sportFilter;
    const matchesType = !typeFilter || event.event_type === typeFilter;
    return matchesSearch && matchesSport && matchesType;
  });

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      
      {/* Hero */}
      <section className="pt-24 pb-12 relative">
        <div className="absolute inset-0 bg-gradient-to-b from-primary/5 to-transparent" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <Link to="/" className="inline-flex items-center gap-2 text-white/60 hover:text-white mb-6" data-testid="back-home">
              <ArrowLeft size={18} />
              <span className="font-body text-sm">Back to Home</span>
            </Link>
            <h1 className="font-heading text-4xl md:text-5xl font-bold text-white mb-4">
              Sports Events Calendar
            </h1>
            <p className="text-white/60 font-body max-w-2xl">
              Discover tournaments, leagues, championships, and sports events across Africa. 
              Filter by sport, location, or date to find what you're looking for.
            </p>
          </motion.div>
        </div>
      </section>
      
      {/* Filters */}
      <section className="pb-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row gap-4">
            {/* Search */}
            <div className="relative flex-1">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-white/40" size={18} />
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search events..."
                className="w-full bg-card border border-border focus:border-primary rounded-md h-12 pl-12 pr-4 text-white font-body outline-none"
                data-testid="events-search"
              />
            </div>
            
            {/* Sport Filter */}
            <div className="relative">
              <Filter className="absolute left-4 top-1/2 -translate-y-1/2 text-white/40" size={18} />
              <select
                value={sportFilter}
                onChange={(e) => setSportFilter(e.target.value)}
                className="bg-card border border-border focus:border-primary rounded-md h-12 pl-12 pr-8 text-white font-body outline-none appearance-none cursor-pointer min-w-[160px]"
                data-testid="events-sport-filter"
              >
                <option value="">All Sports</option>
                {sports.map(sport => (
                  <option key={sport} value={sport}>{sport}</option>
                ))}
              </select>
            </div>
            
            {/* Type Filter */}
            <select
              value={typeFilter}
              onChange={(e) => setTypeFilter(e.target.value)}
              className="bg-card border border-border focus:border-primary rounded-md h-12 px-4 text-white font-body outline-none appearance-none cursor-pointer min-w-[160px]"
              data-testid="events-type-filter"
            >
              <option value="">All Types</option>
              {eventTypes.map(type => (
                <option key={type} value={type}>{type}</option>
              ))}
            </select>
          </div>
        </div>
      </section>
      
      {/* Events Grid */}
      <section className="pb-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {loading ? (
            <div className="flex justify-center py-16">
              <div className="w-10 h-10 border-4 border-primary border-t-transparent rounded-full animate-spin" />
            </div>
          ) : filteredEvents.length === 0 ? (
            <div className="text-center py-16">
              <Trophy className="w-16 h-16 text-white/20 mx-auto mb-4" />
              <p className="text-white/60 font-body">No events found matching your criteria</p>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredEvents.map((event, i) => (
                <motion.div
                  key={event.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: i * 0.05 }}
                  className="bg-card border border-border/40 rounded-xl overflow-hidden card-hover group cursor-pointer"
                  data-testid={`event-card-${event.id}`}
                >
                  <div className="h-40 bg-gradient-to-br from-primary/20 to-secondary/20 flex items-center justify-center relative">
                    <Trophy className="w-16 h-16 text-primary/50" />
                    <span className="absolute top-4 right-4 bg-primary/20 text-primary text-xs font-body px-3 py-1 rounded-full">
                      {event.event_type}
                    </span>
                  </div>
                  <div className="p-6">
                    <span className="text-xs font-body text-secondary uppercase tracking-wider">{event.sport}</span>
                    <h3 className="font-heading text-xl font-bold text-white mt-2 mb-3 group-hover:text-primary transition-colors">
                      {event.name}
                    </h3>
                    <div className="space-y-2 text-white/50 text-sm font-body">
                      <div className="flex items-center gap-2">
                        <Calendar size={14} />
                        <span>
                          {new Date(event.start_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                          {event.end_date && ` - ${new Date(event.end_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`}
                        </span>
                      </div>
                      {event.venue && (
                        <div className="flex items-center gap-2">
                          <MapPin size={14} />
                          <span>{event.venue.name}</span>
                        </div>
                      )}
                    </div>
                    <button className="mt-4 w-full btn-secondary flex items-center justify-center gap-2 text-xs h-10">
                      View Details <ChevronRight size={14} />
                    </button>
                  </div>
                </motion.div>
              ))}
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default EventsPage;
